// This context provider returns wrappers around react-router-dom v6 exports.
// It's exposed as a context/provider so that consumers can share navigation/history/search params state.
// It should NOT be used directly, instead consumers should use @atom/hooks/useNavigation which has
// additional (optional) support for typed search params parsing

import React, {
  createContext,
  ForwardRefExoticComponent,
  PropsWithChildren,
  RefAttributes,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import {
  Link,
  LinkProps,
  NavigateFunction,
  Params,
  To,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import * as R from 'ramda';

export enum SearchParamType {
  NUMBER = 'NUMBER',
  ARRAY = 'ARRAY',
  BOOLEAN = 'BOOLEAN',
  STRING = 'STRING',
}

interface SearchParamsObject {
  [key: string]: string & boolean & number & string[];
}

export type NavigationContextValue = {
  searchParams: URLSearchParams;
  updateSearchParams: (payload: Object, replace?: boolean) => void;
  backDestination: (defaultUrl?: string) => To;
  params: Readonly<Params<string>>;
  pathname: string;
  navigate: NavigateFunction;
  Link: ForwardRefExoticComponent<LinkProps & RefAttributes<HTMLAnchorElement>>;
};

const NOOP = () => {};

const initialContext: NavigationContextValue = {
  searchParams: new URLSearchParams(),
  updateSearchParams: NOOP,
  backDestination: () => '',
  params: {},
  pathname: '',
  navigate: NOOP,
  Link: null,
};

const NavigationContext = createContext<NavigationContextValue>(initialContext);

export const useNavigationContext = () => useContext(NavigationContext);

const NavigationProvider: React.FC<PropsWithChildren<{}>> = ({ children }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // to delete a searchParam, pass it in as null or undefined
  const updateSearchParams = useCallback(
    (payload: SearchParamsObject, replace = true) => {
      const updatesArray = R.keys(payload).map(property => ({
        property,
        value: payload[property],
      }));

      setSearchParams(
        currentParams => {
          updatesArray.forEach(item => {
            if (R.isNil(item.value)) {
              currentParams.delete(String(item.property));
            } else {
              currentParams.set(String(item.property), String(item.value));
            }
          });
          return currentParams;
        },
        { replace },
      );
    },
    [pathname, setSearchParams],
  );

  const backDestination = useCallback((defaultUrl: string = '/'): To => {
    const canGoBack = window?.history?.length > 2;
    const destination: any = canGoBack ? -1 : defaultUrl;
    return destination;
  }, []);

  const value = useMemo<NavigationContextValue>(
    () => ({
      searchParams,
      updateSearchParams,
      backDestination,
      params,
      pathname,
      navigate,
      Link,
    }),
    [
      searchParams,
      updateSearchParams,
      backDestination,
      params,
      pathname,
      navigate,
    ],
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

export default NavigationProvider;
